/**
 * Stripe minimum charge amounts by currency
 * Source: https://stripe.com/docs/currencies#minimum-and-maximum-charge-amounts
 */

export const STRIPE_MINIMUMS: Record<string, number> = {
  // Major currencies (in their smallest unit, e.g., cents for USD)
  USD: 0.50,  // 50 cents
  EUR: 0.50,  // 50 cents
  GBP: 0.30,  // 30 pence
  CAD: 0.50,  // 50 cents
  AUD: 0.50,  // 50 cents
  JPY: 50,    // 50 yen (no decimal)
  CHF: 0.50,  // 50 centimes
  CNY: 4.00,  // 4 yuan
  HKD: 4.00,  // 4 dollars
  SGD: 0.50,  // 50 cents
  SEK: 3.00,  // 3 kronor
  NOK: 3.00,  // 3 kroner
  DKK: 2.50,  // 2.50 kroner
  PLN: 2.00,  // 2 złoty
  CZK: 15.00, // 15 korun
  HUF: 175,   // 175 forint
  RON: 2.00,  // 2 lei
  BGN: 1.00,  // 1 lev
  HRK: 5.00,  // 5 kuna
  RUB: 50,    // 50 rubles
  TRY: 20.00, // 20 lira
  INR: 50,    // 50 rupees
  BRL: 0.50,  // 50 centavos
  ZAR: 10,    // 10 rand
  MXN: 10,    // 10 pesos
  NZD: 0.50,  // 50 cents
  THB: 20,    // 20 baht
  MYR: 2.00,  // 2 ringgit
  PHP: 20,    // 20 pesos
  IDR: 10000, // 10,000 rupiah
  KRW: 500,   // 500 won
  VND: 10000, // 10,000 dong
  AED: 2.00,  // 2 dirhams
  SAR: 2.00,  // 2 riyals
  // Default for unknown currencies
  DEFAULT: 0.50
};

/**
 * Get the minimum charge amount for a currency
 * @param currency - The currency code (e.g., 'USD')
 * @returns The minimum amount in the currency's major unit (e.g., dollars for USD)
 */
export function getStripeMinimum(currency: string): number {
  const upperCurrency = currency.toUpperCase();
  return STRIPE_MINIMUMS[upperCurrency] || STRIPE_MINIMUMS.DEFAULT;
}

/** True when Stripe can charge this ISO currency (CHF, USD, EUR, …). */
export function stripeSupportsCurrency(currency: string): boolean {
  const code = (currency || '').toUpperCase();
  return Boolean(code) && code !== 'DEFAULT' && Object.prototype.hasOwnProperty.call(STRIPE_MINIMUMS, code);
}

export function currencyListIncludes(list: string[] | undefined, currency: string): boolean {
  const code = (currency || '').toUpperCase();
  return Boolean(code) && (list ?? []).some((item) => String(item).toUpperCase() === code);
}

/**
 * Check if an amount meets Stripe's minimum requirement
 * @param amount - The amount to check
 * @param currency - The currency code
 * @returns true if the amount meets the minimum requirement
 */
export function meetsStripeMinimum(amount: number, currency: string): boolean {
  const minimum = getStripeMinimum(currency);
  return amount >= minimum;
}

/**
 * Format a minimum amount message for display
 * @param currency - The currency code
 * @returns A formatted message about the minimum amount
 */
export function getStripeMinimumMessage(currency: string): string {
  const minimum = getStripeMinimum(currency);
  const upperCurrency = currency.toUpperCase();
  
  // Format based on currency conventions
  if (upperCurrency === 'JPY' || upperCurrency === 'KRW' || upperCurrency === 'HUF' || 
      upperCurrency === 'IDR' || upperCurrency === 'VND') {
    // Currencies without decimal places
    return `Minimum payment amount is ${minimum} ${upperCurrency}`;
  }
  
  return `Minimum payment amount is ${minimum.toFixed(2)} ${upperCurrency}`;
}

export { toStripeMinorUnits } from '@vaiu/utils';
